import { useState, useEffect, useMemo, useRef } from "react";
import { SHIFTIQ_API, getAuthHeaders, isAbortError } from "../api/client";
import { leaveStoreNamesForUser, sortLeaveStoreNames } from "../storeBrand";
import { isSelectableStore } from "../theme";
import { locationIdForStore, locationScopeQuery } from "../lib/storeScope";
import { filterOpsTestEmployees } from "../lib/softone";
import { userEmployeeCode } from "../lib/userDisplay";
import { isEmployeeRole } from "../identity/access";

export function useShiftIQ(store, weekStart, unifiedLocationId = null) {
  const [recommendations, setRecommendations] = useState(null);
  const [demandIntensity, setDemandIntensity]  = useState(null);
  const [hourlyTargets, setHourlyTargets] = useState(null);
  const [weeklyTargets, setWeeklyTargets] = useState(null);
  const [weeklyTargetsGeneratedAt, setWeeklyTargetsGeneratedAt] = useState(null);
  const [shiftiqLoadError, setShiftiqLoadError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  useEffect(() => {
    if (!isSelectableStore(store) || unifiedLocationId == null) {
      setRecommendations(null);
      setDemandIntensity(null);
      setHourlyTargets(null);
      setWeeklyTargets(null);
      setWeeklyTargetsGeneratedAt(null);
      setShiftiqLoadError(false);
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    setShiftiqLoadError(false);

    const locQs = locationScopeQuery(unifiedLocationId);
    function getMonday() {
      const d = new Date();
      const day = d.getDay();
      const diff = d.getDate() - (day === 0 ? 6 : day - 1);
      return new Date(d.setDate(diff)).toISOString().slice(0, 10);
    }
    const month = (weekStart || getMonday()).slice(0, 7);

    const opts = { headers: getAuthHeaders(), signal: controller.signal };
    const quiet = (err) => {
      if (isAbortError(err)) throw err;
      console.warn("ShiftIQ fetch failed:", err);
      return null;
    };
    Promise.all([
      fetch(`${SHIFTIQ_API}/shiftiq/recommendations?month=${month}&${locQs}`, opts)
        .then(r => r.ok ? r.json() : null)
        .catch(quiet),
      fetch(`${SHIFTIQ_API}/shiftiq/demand?${locQs}`, opts)
        .then(r => r.ok ? r.json() : null)
        .catch(quiet),
      fetch(`${SHIFTIQ_API}/shiftiq/hourly-targets?week_start=${weekStart}&${locQs}`, opts)
        .then(r => r.ok ? r.json() : null)
        .catch(quiet),
      fetch(`${SHIFTIQ_API}/shiftiq/weekly?week_start=${weekStart}&${locQs}`, opts)
        .then(r => r.ok ? r.json() : null)
        .catch(quiet),
    ])
      .then(([recs, demand, targets, weekly]) => {
        if (controller.signal.aborted) return;
        setRecommendations(recs);
        setDemandIntensity(demand?.intensity ?? null);
        setHourlyTargets(targets?.hours ?? null);
        setWeeklyTargets(weekly?.days ?? null);
        setWeeklyTargetsGeneratedAt(weekly?.generated_at ?? null);
        if (!recs && !demand && !targets && !weekly) setShiftiqLoadError(true);
      })
      .catch((err) => {
        if (controller.signal.aborted || isAbortError(err)) return;
        setError("ShiftIQ unavailable");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [store, weekStart, unifiedLocationId]);

  return { recommendations, demandIntensity, hourlyTargets, weeklyTargets, weeklyTargetsGeneratedAt, shiftiqLoadError, loading, error };
}

export function usePeopleCost(store, weekStart, refetchTick = 0, unifiedLocationId = null, unifiedBrandId = null) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isSelectableStore(store) || !weekStart || unifiedLocationId == null) return;
    const controller = new AbortController();
    setLoading(true);
    setData(null);
    const locQs = locationScopeQuery(unifiedLocationId);
    fetch(`${SHIFTIQ_API}/people-cost?${locQs}&week_start=${weekStart}${unifiedBrandId != null ? `&unified_brand_id=${encodeURIComponent(unifiedBrandId)}` : ""}`, { headers: getAuthHeaders(), signal: controller.signal })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (!controller.signal.aborted) setData(d); })
      .catch((err) => {
        if (controller.signal.aborted || isAbortError(err)) return;
        setData(null);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [store, weekStart, refetchTick, unifiedLocationId, unifiedBrandId]);

  return { data, loading };
}

export function useHeadcountPlanning(store, unifiedLocationId = null, unifiedBrandId = null) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // null = explicit network view. "" / missing = no store yet — do not fetch.
    if (store === "" || store === undefined) {
      setData(null);
      setLoading(false);
      return;
    }
    if (store !== null && unifiedLocationId == null) {
      setData(null);
      setLoading(false);
      return;
    }
    if (store && !isSelectableStore(store)) {
      setData(null);
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    setLoading(true);
    setData(null);
    const locQs = locationScopeQuery(unifiedLocationId);
    const brandQs = unifiedBrandId != null ? `&unified_brand_id=${encodeURIComponent(unifiedBrandId)}` : "";
    const url = store === null
      ? `${SHIFTIQ_API}/headcount-planning`
      : `${SHIFTIQ_API}/headcount-planning?${locQs}${brandQs}`;
    fetch(url, { headers: getAuthHeaders(), signal: controller.signal })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (!controller.signal.aborted) setData(d); })
      .catch((err) => {
        if (controller.signal.aborted || isAbortError(err)) return;
        setData(null);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [store, unifiedLocationId, unifiedBrandId]);

  return { data, loading };
}

export function useStaff(storeId, { allowUnscoped = false, unifiedLocationId = null } = {}) {
  const [staff, setStaff] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const scoped = isSelectableStore(storeId);
    if (!scoped && !allowUnscoped) {
      setStaff(null);
      setError(null);
      setLoading(false);
      return;
    }
    if (scoped && unifiedLocationId == null) {
      setStaff(null);
      setError(null);
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    const qs = unifiedLocationId != null
      ? `?${locationScopeQuery(unifiedLocationId)}`
      : "";
    fetch(`${SHIFTIQ_API}/staff${qs}`, { headers: getAuthHeaders(), signal: controller.signal })
      .then(async (r) => {
        const data = await r.json().catch(() => null);
        if (!r.ok) {
          const detail = data && data.detail;
          const message = typeof detail === "string"
            ? detail
            : (detail && detail.message) || "Could not load staff data";
          throw new Error(message);
        }
        if (!Array.isArray(data)) throw new Error("Could not load staff data");
        return data;
      })
      .then(data => {
        if (!controller.signal.aborted) setStaff(filterOpsTestEmployees(data));
      })
      .catch((err) => {
        if (controller.signal.aborted || isAbortError(err)) return;
        setStaff(null);
        setError(err?.message || "Could not load staff data");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [storeId, allowUnscoped, unifiedLocationId]);

  return { staff, loading, error };
}

export function useLeaveRequests(store, user, locationNames, storeRecords, brands) {
  const names = useMemo(() => {
    const scoped = leaveStoreNamesForUser(user, locationNames, storeRecords, brands);
    if (scoped.length > 0) return scoped;
    if (locationNames && locationNames.length > 0) {
      return sortLeaveStoreNames(locationNames, storeRecords, brands);
    }
    return [];
  }, [locationNames, user, storeRecords, brands]);
  const [reqs, setReqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const leaveAbortRef = useRef(null);

  const storeId = isEmployeeRole(user) ? user?.store : store;

  function load() {
    leaveAbortRef.current?.abort();
    if (!user || !storeId) {
      setReqs([]);
      setLoading(false);
      return Promise.resolve([]);
    }

    const controller = new AbortController();
    leaveAbortRef.current = controller;
    const { signal } = controller;
    setLoading(true);
    const storesToLoad = storeId === "All Stores" ? names : [storeId];
    return Promise.all(
      storesToLoad.map(s => {
        const locId = locationIdForStore(s, storeRecords);
        if (locId == null) return Promise.resolve([]);
        return fetch(`${SHIFTIQ_API}/leave-requests?${locationScopeQuery(locId)}`, {
          headers: getAuthHeaders(),
          signal,
        }).then(r => r.ok ? r.json() : []);
      })
    )
      .then(results => {
        if (signal.aborted) return [];
        const data = results.flat();
        setReqs(data);
        return data;
      })
      .catch((err) => {
        if (signal.aborted || isAbortError(err)) return [];
        setReqs([]);
        return [];
      })
      .finally(() => {
        if (!signal.aborted) setLoading(false);
      });
  }

  useEffect(() => {
    load();
    return () => leaveAbortRef.current?.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load() closes over latest store list
  }, [storeId, user?.id, user?.email, names]);

  function submitOne(type, date, reason, options = {}) {
    const body = {
      store_id: storeId,
      employee_name: user.name,
      employee_email: user.email,
      type,
      date,
      reason,
      request_week_start: options.request_week_start,
    };
    if (type === "shift_preference") body.shift_preference = options.shift_preference;
    body.document_urls = options.document_urls ?? [];
    const empCode = userEmployeeCode(user);
    if (empCode) body.employee_code = empCode;

    return fetch(`${SHIFTIQ_API}/leave-requests`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
    }).then((r) => (r.ok ? r.json() : Promise.reject(date)));
  }

  function attachDocuments(id, documentUrls) {
    return fetch(`${SHIFTIQ_API}/leave-requests/${id}/documents`, {
      method: "PATCH",
      headers: getAuthHeaders(),
      body: JSON.stringify({ document_urls: documentUrls }),
    }).then(async (r) => {
      if (!r.ok) {
        const payload = await r.json().catch(() => null);
        throw new Error(payload?.detail || "Upload failed");
      }
      return r.json();
    }).then(() => load());
  }

  function markDocumentationReceived(id, reason) {
    return fetch(`${SHIFTIQ_API}/leave-requests/${id}/documentation-status`, {
      method: "PATCH",
      headers: getAuthHeaders(),
      body: JSON.stringify({
        documentation_status: "received",
        reason,
        reviewed_by: user.name,
      }),
    }).then(async (r) => {
      if (!r.ok) {
        const payload = await r.json().catch(() => null);
        const detail = payload?.detail;
        const message =
          (typeof detail === "object" && detail?.message) ||
          (typeof detail === "string" ? detail : null) ||
          "Override failed";
        throw new Error(message);
      }
      return r.json();
    }).then(() => load());
  }

  function markSoftoneEntered(id, reason) {
    return fetch(`${SHIFTIQ_API}/leave-requests/${id}/softone-entry`, {
      method: "PATCH",
      headers: getAuthHeaders(),
      body: JSON.stringify({
        needs_softone_entry: false,
        reason,
        reviewed_by: user.name,
      }),
    }).then(async (r) => {
      if (!r.ok) {
        const payload = await r.json().catch(() => null);
        const detail = payload?.detail;
        const message =
          (typeof detail === "object" && detail?.message) ||
          (typeof detail === "string" ? detail : null) ||
          "SoftOne entry clear failed";
        throw new Error(message);
      }
      return r.json();
    }).then(() => load());
  }

  function markOfficialFormHandled(id, reason) {
    return fetch(`${SHIFTIQ_API}/leave-requests/${id}/official-form-failure`, {
      method: "PATCH",
      headers: getAuthHeaders(),
      body: JSON.stringify({
        official_form_failed: false,
        reason,
        reviewed_by: user.name,
      }),
    }).then(async (r) => {
      if (!r.ok) {
        const payload = await r.json().catch(() => null);
        const detail = payload?.detail;
        const message =
          (typeof detail === "object" && detail?.message) ||
          (typeof detail === "string" ? detail : null) ||
          "Official form clear failed";
        throw new Error(message);
      }
      return r.json();
    }).then(() => load());
  }

  function submit(type, date, reason, options = {}) {
    return submitOne(type, date, reason, options).then(() => load());
  }

  function decide(id, status, reviewNotesOrOptions) {
    let reviewNotes;
    let overrideConfirmed = false;
    if (
      reviewNotesOrOptions != null &&
      typeof reviewNotesOrOptions === "object" &&
      !Array.isArray(reviewNotesOrOptions)
    ) {
      reviewNotes = reviewNotesOrOptions.reviewNotes;
      overrideConfirmed = Boolean(reviewNotesOrOptions.overrideConfirmed);
    } else {
      reviewNotes = reviewNotesOrOptions;
    }

    const body = {
      status,
      reviewed_by: user.name,
    };
    if (reviewNotes !== undefined) body.review_notes = reviewNotes;
    if (overrideConfirmed) body.override_confirmed = true;

    return fetch(`${SHIFTIQ_API}/leave-requests/${id}`, {
      method: "PATCH",
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
    })
      .then(async (r) => {
        const payload = await r.json().catch(() => null);
        const detail = payload?.detail;
        if (r.status === 409 && detail?.exceeds_balance) {
          const err = new Error(
            detail.message || "Approval exceeds available leave balance"
          );
          err.code = "EXCEEDS_BALANCE";
          err.detail = detail;
          throw err;
        }
        if (r.status === 409 && detail?.staff_resolution_failed) {
          const err = new Error(
            detail.message ||
              "Cannot verify leave balance — employee not found in store staff records"
          );
          err.code = "STAFF_RESOLUTION_FAILED";
          err.detail = detail;
          throw err;
        }
        if (r.status === 403 && detail?.override_role_allowed === false) {
          const err = new Error(
            detail.message || "Only admin and HR can approve leave balance overrides"
          );
          err.code = "OVERRIDE_FORBIDDEN";
          err.detail = detail;
          throw err;
        }
        if (r.status === 400 && detail?.override_note_valid === false) {
          const err = new Error(
            detail.message || "Override note must be at least 10 characters with a substantive reason"
          );
          err.code = "OVERRIDE_NOTE_INVALID";
          err.detail = detail;
          throw err;
        }
        if (!r.ok) {
          const message =
            typeof detail === "string"
              ? detail
              : detail?.message || "Leave request update failed";
          throw new Error(message);
        }
        return payload;
      })
      .then((data) => {
        if (status === "approved" && storeId && storeId !== "All Stores") {
          window.dispatchEvent(
            new CustomEvent("rota-updated", {
              detail: { store_id: storeId, refreshGantt: false },
            })
          );
        }
        return load().then(() => data);
      });
  }

  function remove(id) {
    const params = new URLSearchParams({
      employee_name: user.name,
    });
    if (user.email) params.set("employee_email", user.email);

    return fetch(`${SHIFTIQ_API}/leave-requests/${id}?${params.toString()}`, {
      method: "DELETE",
      headers: getAuthHeaders(),
    })
      .then(r => r.ok ? r.json() : null)
      .then(() => load());
  }

  function sampleLeaveError(data, fallback) {
    const detail = data?.detail;
    return new Error(typeof detail === "string" ? detail : fallback);
  }

  function createSamples() {
    return fetch(`${SHIFTIQ_API}/leave-requests/samples`, {
      method: "POST",
      headers: getAuthHeaders(),
    }).then(async (r) => {
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw sampleLeaveError(data, "Could not add sample leave");
      return load().then(() => data);
    });
  }

  function deleteSamples() {
    return fetch(`${SHIFTIQ_API}/leave-requests/samples`, {
      method: "DELETE",
      headers: getAuthHeaders(),
    }).then(async (r) => {
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw sampleLeaveError(data, "Could not delete sample leave");
      return load().then(() => data);
    });
  }

  return {
    reqs,
    loading,
    submit,
    submitOne,
    decide,
    remove,
    createSamples,
    deleteSamples,
    load,
    attachDocuments,
    markDocumentationReceived,
    markSoftoneEntered,
    markOfficialFormHandled,
  };
}